// mindmetal-app/web/src/components/mindmetal/PreferencesList.tsx
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronRight, Star, Eye } from 'lucide-react'
import { Badge, cn } from '@personic/shared'

interface PreferencesListProps {
  preferences: Record<string, unknown>
  className?: string
}

interface PreferenceItemData {
  value?: unknown
  source?: 'inferred' | 'explicit'
  confidence?: number
}

export function PreferencesList({ preferences, className }: PreferencesListProps) {
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set())

  const toggleDomain = (domain: string) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev)
      if (next.has(domain)) {
        next.delete(domain)
      } else {
        next.add(domain)
      }
      return next
    })
  }

  const domains = Object.entries(preferences || {})

  if (domains.length === 0) {
    return (
      <div className={cn('text-center py-8 text-warm-500', className)}>
        No preferences extracted yet
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      {domains.map(([domain, items]) => {
        const isExpanded = expandedDomains.has(domain)
        const itemsObj = items as Record<string, unknown> | unknown[]
        const itemCount = Array.isArray(itemsObj)
          ? itemsObj.length
          : Object.keys(itemsObj || {}).length

        return (
          <div key={domain} className="border border-warm-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleDomain(domain)}
              className="w-full flex items-center justify-between p-3 bg-warm-50 hover:bg-warm-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-warm-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-warm-500" />
                )}
                <span className="font-medium text-midnight-800 capitalize">
                  {domain.replace(/_/g, ' ')}
                </span>
              </div>
              <Badge variant="default" size="sm">
                {itemCount}
              </Badge>
            </button>

            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-3 space-y-2 bg-white">
                    {renderItems(itemsObj)}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}

function renderItems(items: Record<string, unknown> | unknown[]) {
  if (Array.isArray(items)) {
    return items.map((item, i) => renderItem(String(i), item))
  }
  return Object.entries(items || {}).map(([key, val]) => renderItem(key, val))
}

function renderItem(key: string, val: unknown) {
  const pref = (typeof val === 'object' && val !== null ? val : { value: val }) as PreferenceItemData
  const displayValue = pref.value !== undefined ? String(pref.value) : String(val)
  const displayKey = isNaN(Number(key)) ? key.replace(/_/g, ' ') : displayValue

  return (
    <div
      key={key}
      className="flex items-center justify-between py-2 px-3 bg-warm-50 rounded-lg"
    >
      <div className="flex items-center gap-2">
        {pref.source === 'explicit' ? (
          <Star className="w-3 h-3 text-copper-500" />
        ) : (
          <Eye className="w-3 h-3 text-warm-400" />
        )}
        <span className="text-sm text-midnight-700">{displayKey}</span>
      </div>
      {pref.confidence !== undefined && (
        <span className="text-xs text-warm-500">{(pref.confidence * 100).toFixed(0)}%</span>
      )}
    </div>
  )
}
